import { Badge, Popover, TextInput } from '@cartwheel/components'
import { useEffect, useMemo, useRef, useState } from 'react'

interface Organization {
  id: string
  name: string
}

interface DistrictFilterProps {
  organizations: Organization[]
  selectedOrganizationId: string
  showSelectAll: boolean
  dashboardPath: string
}

const SELECT_ALL_VALUE = 'all'

export default function DistrictFilter({
  organizations,
  selectedOrganizationId,
  showSelectAll,
  dashboardPath,
}: DistrictFilterProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      // Delay focus slightly to ensure the popover content is rendered
      requestAnimationFrame(() => {
        searchInputRef.current?.focus()
      })
    }
  }, [open])

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setSearchQuery('')
    }
  }

  const handleSelect = (value: string) => {
    const url = new URL(dashboardPath, window.location.origin)
    url.searchParams.set('organization_id', value)
    window.location.assign(url.toString())
  }

  const filteredOrganizations = useMemo(() => {
    if (!searchQuery.trim()) return organizations
    const query = searchQuery.toLowerCase()
    return organizations.filter((org) => org.name.toLowerCase().includes(query))
  }, [organizations, searchQuery])

  const triggerLabel =
    selectedOrganizationId === SELECT_ALL_VALUE
      ? 'Select All'
      : (organizations.find((o) => o.id === selectedOrganizationId)?.name ?? 'Select district')

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger>
        <Badge label={triggerLabel} color="mint" rightIcon="caret-down" />
      </Popover.Trigger>
      <Popover.Content side="bottom" align="end" className="!p-0 min-w-[16rem]">
        <div className="px-3 pt-3 pb-2">
          <TextInput
            ref={searchInputRef}
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="overflow-y-auto max-h-60">
          {showSelectAll && (
            <>
              <button
                type="button"
                className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-sand ${
                  selectedOrganizationId === SELECT_ALL_VALUE ? 'bg-mint-25' : ''
                }`}
                onClick={() => handleSelect(SELECT_ALL_VALUE)}
              >
                <div>
                  <div className="text-sm font-semibold text-forest">Select All</div>
                  <div className="text-xs text-forest-light">
                    Applies to Action Items table only
                  </div>
                </div>
                {selectedOrganizationId === SELECT_ALL_VALUE && (
                  <i className="fas fa-check text-forest text-xs flex-shrink-0" />
                )}
              </button>
              <div className="border-t border-gray-stroke" />
            </>
          )}
          {filteredOrganizations.map((org) => (
            <button
              key={org.id}
              type="button"
              className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-sand ${
                org.id === selectedOrganizationId ? 'bg-mint-25' : ''
              }`}
              onClick={() => handleSelect(org.id)}
            >
              <span className="text-sm text-charcoal">{org.name}</span>
              {org.id === selectedOrganizationId && (
                <i className="fas fa-check text-forest text-xs flex-shrink-0" />
              )}
            </button>
          ))}
          {filteredOrganizations.length === 0 && (
            <div className="px-4 py-3 text-sm text-charcoal-50">No results</div>
          )}
        </div>
      </Popover.Content>
    </Popover.Root>
  )
}
